import { PillLink } from "./PillLink";
import { routes } from "./landingContent";

interface LandingHeroProps {
  /** Wide community photo for the top-right cell. Falls back to a plain tile. */
  imageUrl?: string | null;
}

/**
 * Hero: blue panel spanning both rows on the left, community photo top-right,
 * green statement block bottom-right.
 *
 * Collapses to a single column below lg — blue panel, then photo, then green
 * block, per the handoff's responsive note.
 */
export default function LandingHero({ imageUrl }: LandingHeroProps) {
  return (
    <section className="mx-auto w-full max-w-[1440px] p-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] lg:grid-rows-[360px_auto]">
        {/* Blue panel */}
        <div className="flex flex-col justify-between rounded-[28px] bg-eab-blue-dark px-6 py-12 text-white sm:px-10 lg:row-span-2 lg:px-14 lg:pb-14 lg:pt-16">
          <div>
            <span className="mb-7 inline-flex items-center rounded-full bg-white/[0.14] px-4 py-[9px] text-[15px] font-semibold text-eab-green">
              Entrepreneurs Across Borders
            </span>
            <h1 className="mb-[26px] text-pretty text-[44px] font-extrabold leading-none tracking-[-0.035em] sm:text-[60px] lg:text-[82px]">
              Tools, Mentors and <span className="text-eab-orange">Resources</span> — in one friendly
              place.
            </h1>
            <p className="max-w-[560px] text-pretty text-[19px] font-normal leading-[1.5] text-white/90 lg:text-[22px]">
              Empowering entrepreneurs with the tools, connections, community, and confidence to
              build what's next.
            </p>
          </div>
          <div className="mt-11 flex flex-wrap gap-3">
            <PillLink to={routes.signUp} variant="orange">
              Start your journey
            </PillLink>
            <PillLink to={routes.signUpMentor} variant="outlineLight">
              Join as a mentor
            </PillLink>
          </div>
        </div>

        {/* Community photo */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="EAB Connect community"
            className="h-[240px] w-full rounded-[28px] object-cover lg:h-[360px]"
          />
        ) : (
          <div className="h-[240px] w-full rounded-[28px] bg-white lg:h-[360px]" aria-hidden="true" />
        )}

        {/* Green statement block */}
        <div className="flex flex-col justify-end rounded-[28px] bg-eab-green p-8 text-eab-ink lg:p-10">
          <p className="text-pretty text-[32px] font-extrabold leading-[1.1] tracking-[-0.03em] lg:text-[44px]">
            You bring the idea. We'll bring the people.
          </p>
          <p className="mt-4 text-[18px] font-medium text-eab-ink/70">
            Mentors, investors and founders, all in one community.
          </p>
        </div>
      </div>
    </section>
  );
}
